import React, {
    useCallback, useEffect, useMemo, useState,
} from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Grid2, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import DoneIcon from "@mui/icons-material/Done";
import axiosManager from "../../../../../services/axiosManager";
import axiosManagerRegister from "../../../../../services/axiosManagerRegister";
import { useTranslate } from "../../../../../hooks/useTranslate";
import Select from "../../../../../components/Select";
import Preloader from "../../../../../components/Preloader";
import { formatTo } from "../../../../../utils/dateTimeHelper";
import subjectSelectionService from "../../../../../services/subjectSelectionService";
import ReturningSubjectsButton from "../ReturningSubjectsButton";
import Dialog from "../../../../../components/Dialog";
import IconTimeZone from "../../../../../assets/images/icons/timezone.svg?react";
import IconYearGroup from "../../../../../assets/images/icons/yearGroup.svg?react";
import IconStartDate from "../../../../../assets/images/icons/startDate.svg?react";
import IconPackageFull from "../../../../../assets/images/icons/package-full.svg?react";
import IconPackageOther from "../../../../../assets/images/icons/package-other.svg?react";
import IconAlert from "../../../../../assets/images/icons/alert.svg?react";
import IconInfo from "../../../../../assets/images/icons/info.svg?react";
import { useToast } from "../../../../../context/toast";
import "./PackageSelection.css";

const PackageSelection = (props) => {
    const {
        activeStudentId,
        activeStudent,
        studentForename,
        onFinish,
        step,
        divisionSelected,
        yearGroupSelected,
        startDateSelected,
        packageSelected,
        subjectsSelected,
        setStep,
        setDivisionSelected,
        setYearGroupSelected,
        setStartDateSelected,
        setPackageSelected,
        setSubjectsSelected,
        setSummaryText,
    } = props;
    const { t } = useTranslate();
    const toast = useToast();

    const [showCheckModal, setShowCheckModal] = useState(false);

    const {
        isFetching: isLoadingDivisions, data: divisions,
    } = useQuery({
        queryKey: [`students/${activeStudentId}/divisions`],
        queryFn: () => axiosManager.get$("parent/api/divisions").then((response) => response?.data),
        retry: 0,
    });
    const divisionsOptions = useMemo(() => (divisions || []).map((d) => ({ value: d.divisionId, label: d.name })), [divisions]);
    const {
        isFetching: isLoadingYearGroups, data: yearGroups,
    } = useQuery({
        queryKey: [`students/${activeStudentId}/${divisionSelected}/yearGroups`],
        queryFn: () => axiosManager.get$(`parent/api/division/${divisionSelected}/yearGroups`).then((response) => response?.data?.sets),
        retry: 0,
        enabled: !!divisionSelected,
    });
    const yearGroupsOptions = useMemo(() => (yearGroups || []).flatMap(({ name, notionalYearGroupSetId, yearGroups }) => [
        {
            value: notionalYearGroupSetId, label: name, disabled: true, groupType: true,
        },
        ...(yearGroups || []).map((yg) => ({ value: yg.yearGroupId, label: yg.name })),
    ]), [yearGroups]);
    const {
        isFetching: isLoadingStartDates, data: startDates,
    } = useQuery({
        queryKey: [`students/${activeStudentId}/startDates`],
        queryFn: () => axiosManagerRegister.get$("/api/api/startDates").then((response) => response?.data),
        retry: 0,
    });
    const startDatesOptions = useMemo(() => (startDates || []).map((d) => ({ value: d.startDate, label: d.displayName })), [startDates]);

    const { isFetching: isLoadingRecommended, data: recommendedValues } = useQuery({
        queryKey: ["re-enrolment/recommended", activeStudentId],
        queryFn: () => axiosManager.get$(`/parent/api/students/${activeStudentId}/enrolment`).then((response) => response?.data?.student),
        retry: 0,
        refetchOnMount: "always",
        keepPreviousData: false,
    });
    const divisionRecommended = useMemo(() => (divisions || []).find((it) => it.name === recommendedValues?.division), [divisions, recommendedValues]);
    const yearGroupRecommended = useMemo(() => (yearGroupsOptions || []).find((it) => it.label === recommendedValues?.yearGroupName), [yearGroupsOptions, recommendedValues]);
    const startDateRecommended = useMemo(() => (startDatesOptions || []).find((it) => formatTo(it.value, "yyyy-MM-dd") === recommendedValues?.requestedStartDate), [startDatesOptions, recommendedValues]);

    useEffect(() => {
        if (!isLoadingRecommended && divisionRecommended) setDivisionSelected(divisionRecommended?.divisionId);
    }, [isLoadingRecommended, divisionRecommended]);
    useEffect(() => {
        if (!isLoadingRecommended && yearGroupRecommended) setYearGroupSelected(yearGroupRecommended?.value);
    }, [isLoadingRecommended, yearGroupRecommended]);
    useEffect(() => {
        if (!isLoadingRecommended && startDateRecommended) {
            setStartDateSelected(startDateRecommended?.value);
            setStartDate(formatTo(startDateRecommended?.value, "yyyy-MM-dd"));
        }
    }, [isLoadingRecommended, startDateRecommended]);

    const {
        isFetching: isLoadingProduct, data: product,
    } = useQuery({
        queryKey: [`students/${activeStudentId}/product`, yearGroupSelected, startDateSelected, divisionSelected],
        queryFn: () => axiosManager.get$(`/parent/api/yearGroups/${yearGroupSelected}/packages?startDate=${formatTo(startDateSelected, "yyyy-MM-dd")}`).then((response) => response?.data?.packages),
        retry: 0,
        enabled: step > 0,
    });

    const packageProduct = (product || []).find((item) => item.productId === packageSelected);

    useEffect(() => {
        if (!isLoadingProduct && packageSelected && !packageProduct) setStep(1);
    }, [isLoadingProduct, packageSelected, packageProduct]);

    const { mutateAsync: setStartDate, isPending: isSettingStartDate } = useMutation({
        mutationFn: (startDate) => axiosManager.patch$(`/parent/api/students/${activeStudentId}`, { DesiredStartDate: startDate }),
        onError: () => toast.error(t("common.errorMessage")),
    });
    const { mutateAsync: saveSubjects, isPending: isSavingSubjects } = useMutation({
        mutationFn: () => axiosManager.post$(`/parent/api/students/${activeStudentId}/subjects?suppressEvents=true`, {
            divisionId: divisionSelected,
            yearGroupId: yearGroupSelected,
            package: {
                name: packageProduct?.name,
                productId: packageSelected,
            },
            subjects: subjectSelectionService.getSelectedSubjects(packageProduct, subjectsSelected).map((s) => ({
                name: s.name,
                productId: s.productId,
                category: s.sectionName,
            })),
        }),
        onError: () => toast.error(t("common.errorMessage")),
    });

    const packageDescriptionByCategory = useCallback((category) => {
        if (category === "Core") return t("reEnrol.packageFullTimeDescription");
        if (category === "HomeSchooling") return t("reEnrol.package3PackageDescription");
        if (category === "IB") return t("reEnrol.packageIBDescription");
        return "";
    }, [t]);
    const subjectSections = useMemo(() => {
        if (!packageSelected) return [];
        return subjectSelectionService.getSections(packageProduct);
    }, [packageProduct, packageSelected]);
    const areRequiredSelected = useMemo(() => {
        if (!packageSelected) return false;
        return subjectSelectionService.checkIfRequiredSelected(packageProduct, subjectsSelected);
    }, [packageProduct, packageSelected, subjectsSelected]);
    const hasHigherSubjects = useMemo(() => {
        if (!packageSelected) return false;
        return subjectSelectionService.hasHigherSubjects(packageProduct, subjectsSelected);
    }, [packageProduct, packageSelected, subjectsSelected]);
    const higherSubjectsToSelect = useMemo(() => {
        if (!packageSelected) return false;
        return subjectSelectionService.getHigherSubjectsToSelect(packageProduct, subjectsSelected);
    }, [packageProduct, packageSelected, subjectsSelected]);
    const selectedSubjectsArr = useMemo(() => {
        if (!packageSelected) return false;
        return subjectSelectionService.getSelectedSubjects(packageProduct, subjectsSelected);
    }, [packageProduct, packageSelected, subjectsSelected]);

    const summaryText = useMemo(() => {
        const divisionText = divisionsOptions.find((it) => it.value === divisionSelected)?.label;
        const yearText = yearGroupsOptions.find((it) => it.value === yearGroupSelected)?.label;
        const subjects = (selectedSubjectsArr || []).map((s) => s.name).join(" | ");

        return [divisionText, yearText, subjects].join(" • ");
    }, [divisionSelected, divisionsOptions, yearGroupsOptions, yearGroupSelected, selectedSubjectsArr]);

    useEffect(() => {
        if (summaryText) setSummaryText(summaryText);
    }, [summaryText, setSummaryText]);

    const onPackageClick = (packageId) => {
        setPackageSelected(packageId);

        const packageToSet = (product || []).find((item) => item.productId === packageId);
        const lockedSubjects = packageToSet ? subjectSelectionService.getLockedSubjects(packageToSet) : [];
        setSubjectsSelected(lockedSubjects.map((x) => x.productId));
    };
    const handleSubjectSelection = (subjectId) => {
        if (subjectsSelected.includes(subjectId)) {
            setSubjectsSelected(subjectsSelected.filter((item) => item !== subjectId));
        } else {
            setSubjectsSelected([...subjectsSelected, subjectId]);
        }
    };
    const onSubjectClick = (subject) => {
        const isSelectedHigher = subject.higherSubject && subjectsSelected.includes(subject.higherSubject.productId);

        if (isSelectedHigher && subject.higherSubject) {
            handleSubjectSelection(subject.higherSubject.productId);
        } else {
            handleSubjectSelection(subject.productId);
        }
    };
    const onHigherSubjectClick = (subject, isHigher = false) => () => {
        const { higherSubject } = subject;

        if (isHigher) {
            setSubjectsSelected([...subjectsSelected.filter((item) => item !== subject.productId), higherSubject.productId]);
        } else {
            setSubjectsSelected([...subjectsSelected.filter((item) => item !== higherSubject.productId), subject.productId]);
        }
    };

    const onNextToPackage = () => {
        setStep(1);
    };
    const onNextToSubjects = () => {
        setStep(2);
    };
    const onSaveExit = () => {
    };
    const onSaveAndFinish = async () => {
        await saveSubjects();
        onFinish();
    };
    const onNextAfterSubjects = () => {
        if (hasHigherSubjects) setStep(3);
        else onSaveAndFinish();
    };
    const onNextAfterHigher = () => {
        onSaveAndFinish();
    };

    const modalCheck = useMemo(() => {
        if (!packageSelected) return null;
        const {
            hasAbsoluteMaxSubjects,
            hasIncludedMaxSubjects,
            hasRecommendedMaxSubjects,
        } = subjectSelectionService.checkLimits(packageProduct, subjectsSelected);
        const { includedMaxSubjects, recommendedMaxSubjects } = packageProduct || {};
        if (hasAbsoluteMaxSubjects) {
            return {
                title: t("subjects.absoluteMaxSubjects.title"),
                description: t("subjects.absoluteMaxSubjects.subtitle", { num: includedMaxSubjects }),
                cancellationText: t("subjects.absoluteMaxSubjects.cancel"),
                confirmationText: t("subjects.absoluteMaxSubjects.confirm"),
                onCancel: () => setShowCheckModal(false),
                onConfirm: onNextAfterSubjects,
                confirmDisabled: !areRequiredSelected,
            };
        }
        if (hasIncludedMaxSubjects) {
            return {
                title: t("subjects.includedMaxSubjectsModal.title"),
                description: t("subjects.includedMaxSubjectsModal.subtitle", { num: includedMaxSubjects }),
                cancellationText: t("subjects.includedMaxSubjectsModal.cancel"),
                confirmationText: t("subjects.includedMaxSubjectsModal.confirm"),
                onCancel: () => setShowCheckModal(false),
                onConfirm: onNextAfterSubjects,
                confirmDisabled: !areRequiredSelected,
            };
        }

        if (hasRecommendedMaxSubjects) {
            return {
                title: t("subjects.recommendedMaxSubjectsModal.title", { num: recommendedMaxSubjects }),
                description: t("subjects.recommendedMaxSubjectsModal.subtitle"),
                dialogType: "error",
                confirmationText: t("subjects.recommendedMaxSubjectsModal.confirm"),
                onConfirm: () => setShowCheckModal(false),
            };
        }

        return null;
    }, [packageSelected, subjectsSelected, t, onNextAfterSubjects]);

    useEffect(() => {
        if (modalCheck) setShowCheckModal(true);
    }, [!!modalCheck]);

    return (
        <div className="rejoin-package-selection">
            <Preloader isLoading={isLoadingRecommended}>
                <Preloader
                    isLoading={isLoadingDivisions || isLoadingYearGroups || isLoadingStartDates}
                    size={40}
                    overflow
                    showInitiallyOverflow
                >
                    <div className="rejoin-package-selection__box">
                        <Grid2 container spacing={3} className="w-full">
                            <Grid2 size={{ xs: 12, md: 6, lg: 4 }}>
                                <div className="rejoin-package-selection__icon">
                                    <IconTimeZone />
                                </div>
                                <Select
                                    className="rejoin-package-selection__select"
                                    label={t("reEnrol.timezone")}
                                    name="timezone"
                                    value={divisionSelected}
                                    options={[{
                                        value: "",
                                        label: t("reEnrol.selectTimezone"),
                                        disabled: true,
                                    }, ...divisionsOptions]}
                                    disabled={isLoadingDivisions}
                                    onChange={setDivisionSelected}
                                />
                                {divisionRecommended ? (
                                    <Typography variant="body2" component="div" className="mt-2">
                                        {t("reEnrol.timezoneRecommended1", { name: studentForename })}
                                        <b>{t("reEnrol.timezoneRecommended2", { value: divisionRecommended?.name })}</b>
                                    </Typography>
                                ) : null}
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 6, lg: 4 }}>
                                <div className="rejoin-package-selection__icon">
                                    <IconYearGroup />
                                </div>
                                <Select
                                    className="rejoin-package-selection__select"
                                    label={t("reEnrol.yearGroup")}
                                    name="yearGroup"
                                    value={yearGroupSelected}
                                    options={yearGroupsOptions}
                                    disabled={isLoadingYearGroups || !divisionSelected}
                                    onChange={setYearGroupSelected}
                                />
                                {yearGroupRecommended ? (
                                    <Typography variant="body2" component="div" className="mt-2">
                                        {t("reEnrol.yearGroupRecommended", { name: studentForename })}
                                        <b>{yearGroupRecommended?.label}</b>
                                    </Typography>
                                ) : null}
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 6, lg: 4 }}>
                                <div className="rejoin-package-selection__icon">
                                    <IconStartDate />
                                </div>
                                <Select
                                    className="rejoin-package-selection__select"
                                    label={t("reEnrol.startDate")}
                                    name="startDate"
                                    value={startDateSelected}
                                    options={[{
                                        value: "",
                                        label: t("reEnrol.selectStartDate"),
                                        disabled: true,
                                    }, ...startDatesOptions]}
                                    disabled={isLoadingStartDates || isSettingStartDate}
                                    onChange={(v) => {
                                        setStartDateSelected(v);
                                        setStartDate(formatTo(v, "yyyy-MM-dd"));
                                    }}
                                />
                                {startDateRecommended ? (
                                    <Typography variant="body2" component="div" className="mt-2">
                                        {t("reEnrol.startDateRecommended", { name: studentForename })}
                                    </Typography>
                                ) : null}
                            </Grid2>
                        </Grid2>
                        {step === 0 && (
                            <Button
                                disabled={!divisionSelected || !yearGroupSelected || !startDateSelected}
                                className="button-accent rejoin-package-selection__next-button mt-8"
                                onClick={onNextToPackage}
                            >
                                {t("button.nextStep")}
                            </Button>
                        )}
                    </div>
                </Preloader>
                {step > 0 && (
                    <Preloader
                        isLoading={isLoadingDivisions || isLoadingYearGroups || isLoadingStartDates || isLoadingProduct}
                        size={40}
                        overflow
                        showInitiallyOverflow
                    >
                        <div className="rejoin-package-selection__box">
                            <Typography variant="h4" component="div" className="text-primary">
                                {t("reEnrol.selectPackage")}
                            </Typography>
                            <Typography variant="body1" component="div" className="text-primary">
                                {t("reEnrol.selectPackageSubtext")}
                            </Typography>
                            <Grid2 container spacing={2} className="w-full mt-4" alignItems="stretch">
                                {(product || []).map((packageProduct, idx) => (
                                    <Grid2 size={{ xs: 12, md: 6 }} key={packageProduct.productId}>
                                        <div
                                            role="button"
                                            tabIndex={idx}
                                            className={`rejoin-package-selection__package ${packageProduct.productId === packageSelected ? "selected" : ""}`}
                                            onClick={() => onPackageClick(packageProduct.productId)}
                                        >
                                            <div className="rejoin-package-selection__package__icon">
                                                {idx === 0 ? <IconPackageFull /> : <IconPackageOther />}
                                            </div>
                                            <div className="rejoin-package-selection__package__main">
                                                <Typography
                                                    variant="h5"
                                                    component="div"
                                                    className="text-primary rejoin-package-selection__package__title"
                                                >
                                                    {packageProduct.name}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    className="rejoin-package-selection__package__description"
                                                >
                                                    {packageDescriptionByCategory(packageProduct.category)}
                                                </Typography>
                                            </div>
                                            <span className="rejoin-package-selection__package__indicator">
                                                {packageProduct.productId === packageSelected && <DoneIcon />}
                                            </span>
                                        </div>
                                    </Grid2>
                                ))}
                            </Grid2>
                            {step === 1 && (
                                <Button
                                    disabled={!packageSelected}
                                    className="button-accent rejoin-package-selection__next-button mt-8"
                                    onClick={onNextToSubjects}
                                >
                                    {t("button.nextStep")}
                                </Button>
                            )}
                        </div>
                    </Preloader>
                )}
                {step > 1 && (
                    <Preloader
                        isLoading={isLoadingDivisions || isLoadingYearGroups || isLoadingStartDates || isLoadingProduct}
                        size={40}
                        overflow
                        showInitiallyOverflow
                    >
                        <div className="rejoin-package-selection__box">
                            {(subjectSections || []).map((section, idx) => (
                                <div key={section.productId} className={`w-full${idx > 0 ? " mt-8" : ""}`}>
                                    <Typography variant="h4" component="div" className="text-primary">
                                        <b className="font-bold">{section.name}</b>
                                    </Typography>
                                    <Typography variant="body1" component="div" className="text-primary rejoin-package-selection__section-description">
                                        {section.instructions}
                                    </Typography>
                                    <div className="flex flex-wrap align-stretch gap-4 mt-4">
                                        {(section.subjects || []).map((subject) => (
                                            <ReturningSubjectsButton
                                                key={subject.productId}
                                                subject={subject}
                                                packageProduct={packageProduct}
                                                subjectsSelected={subjectsSelected}
                                                onSubjectClick={onSubjectClick}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                            {step === 2 && (
                                <div className="flex xs:flex-column align-center justify-between gap-4 mt-8">
                                    <Button
                                        variant="outlined"
                                        disabled={!packageSelected || hasHigherSubjects || !areRequiredSelected}
                                        className="rejoin-package-selection__save-button"
                                        onClick={onSaveExit}
                                    >
                                        {t("reEnrol.saveExit")}
                                    </Button>
                                    <Button
                                        disabled={!packageSelected || !areRequiredSelected}
                                        className="button-accent rejoin-package-selection__next-button"
                                        onClick={onNextAfterSubjects}
                                    >
                                        {t("button.nextStep")}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </Preloader>
                )}
                {step > 2 && (
                    <Preloader
                        isLoading={isLoadingDivisions || isLoadingYearGroups || isLoadingStartDates || isLoadingProduct}
                        size={40}
                        overflow
                        showInitiallyOverflow
                    >
                        <div className="rejoin-package-selection__box">
                            <Typography variant="h4" component="div" className="text-primary">
                                {t("reEnrol.selectHigherSubjects")}
                            </Typography>
                            {(higherSubjectsToSelect || []).map((subject, idx) => (
                                <div className="w-full mt-6" key={subject.productId}>
                                    <Typography variant="h5" component="div">
                                        <b className="font-bold">{subject.name}</b>
                                    </Typography>
                                    <div className="flex flex-wrap align-stretch gap-4 mt-2">
                                        <ReturningSubjectsButton
                                            key={subject.productId}
                                            subject={subject}
                                            packageProduct={packageProduct}
                                            subjectsSelected={subjectsSelected}
                                            isHigherSelection
                                            onSubjectClick={onHigherSubjectClick(subject)}
                                        />
                                        <ReturningSubjectsButton
                                            key={subject.higherSubject.productId}
                                            subject={subject.higherSubject}
                                            packageProduct={packageProduct}
                                            subjectsSelected={subjectsSelected}
                                            isHigherSelection
                                            onSubjectClick={onHigherSubjectClick(subject, true)}
                                        />
                                    </div>
                                </div>
                            ))}
                            <div className="flex xs:flex-column align-center justify-between gap-4 mt-8">
                                <Button
                                    variant="outlined"
                                    disabled={!packageSelected || !areRequiredSelected}
                                    className="rejoin-package-selection__save-button"
                                    onClick={onSaveExit}
                                >
                                    {t("reEnrol.saveExit")}
                                </Button>
                                <Button
                                    disabled={!packageSelected || !areRequiredSelected}
                                    className="button-accent rejoin-package-selection__next-button"
                                    onClick={onNextAfterHigher}
                                >
                                    {t("button.nextStep")}
                                </Button>
                            </div>
                        </div>
                    </Preloader>
                )}
                {modalCheck && (
                    <Dialog
                        open={!!showCheckModal}
                        onClose={() => setShowCheckModal(false)}
                        className={`subject-dialog ${modalCheck.dialogType === "error" ? "subject-dialog--error" : ""}`}
                    >
                        {modalCheck.dialogType === "error"
                            ? <IconAlert className="subject-dialog__icon" />
                            : <IconInfo className="subject-dialog__icon" />}
                        <Typography variant="h5" component="h3" className="subject-dialog__title">
                            {modalCheck.title}
                        </Typography>
                        <Typography variant="body1" component="div" className="subject-dialog__description">
                            {modalCheck.description}
                        </Typography>
                        <div className="subject-dialog__footer">
                            {modalCheck.cancellationText && (
                                <Button
                                    className="button-accent subject-dialog__btn"
                                    onClick={modalCheck.onCancel}
                                >
                                    {modalCheck.cancellationText}
                                </Button>
                            )}
                            {modalCheck.confirmationText && (
                                <Button
                                    className="button-accent subject-dialog__btn"
                                    onClick={modalCheck.onConfirm}
                                    disabled={modalCheck.confirmDisabled}
                                >
                                    {modalCheck.confirmationText}
                                </Button>
                            )}
                        </div>
                    </Dialog>
                )}
            </Preloader>
        </div>
    );
};

export default PackageSelection;
